import { Laptop, Monitor, MonitorSmartphone, Smartphone, Tablet, type LucideIcon } from 'lucide-react';
import { useDevices } from '../../api/queries.ts';
import type { Device } from '../../api/types.ts';
import { RowList } from '../../components/ui/card.tsx';
import { EmptyNote, ErrorNote, Loading, errorMessage } from '../../components/ui/feedback.tsx';
import { cn } from '../../lib/cn.ts';
import { byLastSeen, deviceKind, formatLastSeen, isActive, relativeLastSeen, type DeviceKind } from './devices.ts';

const KIND_ICON: Record<DeviceKind, LucideIcon> = {
  phone: Smartphone,
  tablet: Tablet,
  laptop: Laptop,
  desktop: Monitor,
  other: MonitorSmartphone,
};

/** How many devices, for the section header; null until they are known. */
export function useDeviceCount(): number | null {
  const devices = useDevices();
  return devices.data ? (devices.data.devices ?? []).length : null;
}

/** Where this account is signed in to the app, most recently seen first. */
export function DevicesSection() {
  const devices = useDevices();
  if (devices.isPending) return <Loading label="Loading your devices…" />;
  if (devices.isError) return <ErrorNote title="Could not load your devices">{errorMessage(devices.error)}</ErrorNote>;
  const list = byLastSeen(devices.data.devices ?? []);
  if (list.length === 0) {
    return (
      <EmptyNote icon={<MonitorSmartphone className="h-5 w-5" />} title="No devices yet">
        Sign in to the app on a phone or computer and it shows up here.
      </EmptyNote>
    );
  }
  const now = Date.now();
  return (
    <div className="flex flex-col gap-3">
      <RowList>
        {list.map((device, index) => (
          <DeviceRow key={String(device.device_id ?? index)} device={device} now={now} />
        ))}
      </RowList>
      <p className="px-1 text-sm text-muted">
        Don’t recognise a device? Change the password of this email account with your email provider, then sign out of the app on that device.
      </p>
    </div>
  );
}

function DeviceRow({ device, now }: { device: Device; now: number }) {
  const Icon = KIND_ICON[deviceKind(device)];
  const active = isActive(device.last_seen, now);
  const details = [device.d_manufacturer, device.app_version !== undefined ? `Version ${device.app_version}` : null].filter(Boolean);
  return (
    <li className="flex items-center gap-4 px-5 py-4 sm:px-6">
      <span className={cn('relative flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl', active ? 'bg-ok-soft text-ok' : 'bg-paper text-ink/70')}>
        <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold text-ink">{device.d_model ?? 'Unknown device'}</p>
        {details.length ? <p className="truncate text-sm text-muted">{details.join(', ')}</p> : null}
      </div>
      <p
        className={cn('flex shrink-0 items-center gap-2 text-sm', active ? 'rounded-full bg-ok-soft px-3 py-1 font-semibold text-ok' : 'text-muted')}
        title={formatLastSeen(device.last_seen)}
      >
        {active ? <span className="h-1.5 w-1.5 rounded-full bg-ok" aria-hidden /> : null}
        {relativeLastSeen(device.last_seen, now)}
      </p>
    </li>
  );
}
